import React, { useRef, useState } from "react";
import { Icon, Theme } from "@dspworkplace/ui";
import { engine } from "../../../Utilities";
import { dialog } from "../../../components/Dialog";
import { alert } from "../../../components/Alert";
import moment from "moment";

const useGhostShiftApi = () => {
    const canceler = useRef(() => {});

    // Function to show an alert dialog
    const showAlert = async (text, btnText = "Cancel") => {
        await alert({
            text,
            btnText,
        });
    };

    // Function to show a confirmation dialog
    const showConfirmationDialog = async (dialogMsg) => {
        return await dialog({
            icon: <Icon.Warning color={Theme.colors.warnings.text} />,
            text: dialogMsg,
            buttons: [
                {
                    text: "Cancel",
                    callback: async () => false,
                },
                {
                    type: "primary",
                    text: "Confirm",
                },
            ],
        });
    };

    const handleGhostShift = async ({ stationId, driverId, date, actionName = "", options = [] }) => {
        const stationRaw = localStorage.getItem("station");
        let stationObject = null;
        try {
            stationObject = stationRaw ? JSON.parse(stationRaw) : null;
        } catch {
            stationObject = null;
        }
        const generateMessageTable = (drivers, title) => {
            return `
                <table style="width: 100%; border-collapse: collapse; margin-top: 13px;">
                    <tbody>
                        <tr><th style="border: 1px solid rgb(230, 216, 216); padding: 5px;">Driver's name</th><th style="border: 1px solid rgb(230, 216, 216); padding: 5px;">Station</th></tr>
                        ${drivers
                            .map(
                                (driver) => `
                            <tr>
                                <td style="border: 1px solid rgb(230, 216, 216); padding: 5px;">${driver.name != undefined ? driver.name : driver.driverName}</td>
                                <td style="border: 1px solid rgb(230, 216, 216); padding: 5px;">${driver.code != undefined ? driver.code : driver.stationCode}</td>
                            </tr>
                        `
                            )
                            .join("")}
                    </tbody>
                </table>`;
        };

        let driverIds = Array.isArray(driverId) ? driverId : [driverId];
        const response = await engine().post("/api/lazy/manage/data", {
            actions: {
                response: {
                    Driver: {
                        custom: {
                            functionName: "checkDriverRoute",
                            get: "result",
                            excludes: [
                                "stations",
                                "employmentStatus",
                                "user",
                                "assignedManagers",
                                "payRate",
                                "company",
                                "schedules",
                                "cortexLogs",
                            ],
                            criteria: { stationId, checkIncident: true, driverIds: driverIds, date },
                        },
                    },
                },
            },
        });

        let { availableDriverIds, isTodayPast, success } = response.data.data.result;
        // is ghost shift
        const resGhostShift = await engine().post("/api/lazy/manage/data", {
            actions: {
                response: {
                    DriverRoute: {
                        custom: {
                            functionName: "checkShiftIsGhost",
                            get: "result",
                            criteria: { stationId:stationId, shiftDriverId:driverIds, shiftDate:date, type:"ADD_DRIVER" },
                        },
                    },
                },
            },
        });
        let dialogMsg = "";
        const ghostShiftDrivers = Array.isArray(resGhostShift.data?.data?.result) ? resGhostShift.data.data.result : [];
        
        if (actionName == "SCHEDULER_ADD_ROUTE_WITH_DATE" || actionName == "SCHEDULER_ADD_ROUTE") {
            if (ghostShiftDrivers.length > 0) {
                dialogMsg = `Some selected drivers already have scheduled ghost shifts at other stations. You cannot create shifts for them. The remaining drivers' shifts will be created after clicking the Cancel button.`+generateMessageTable(ghostShiftDrivers, "Publish Drivers");
                await alert({ text: dialogMsg, btnText: "Cancel" });
            }
            let ignoreGhostShiftDriverId = ghostShiftDrivers.map((driver) => driver.driverId);
            availableDriverIds = availableDriverIds.filter(item => !ignoreGhostShiftDriverId.includes(item.driverId));
            if (availableDriverIds.length > 0 && isTodayPast) {
                dialogMsg = `Driver ${availableDriverIds[0].name} already has a scheduled shift in ${availableDriverIds[0].code}. You can't move a shift that already happened.`;
                await alert({
                    text: dialogMsg,
                    btnText: "Cancel",
                });
                return false;
            }

            if (success && availableDriverIds.length > 0) {
                let publishDriverId = [];
                let availableDriverId = availableDriverIds.map((driver) => driver.driverId);
                let filterAvailableDriverIds = driverIds
                    .filter((item) => !availableDriverId.includes(Number(item)))
                    .map(Number);
                if (ignoreGhostShiftDriverId.length > 0) {
                    let integerIds = ignoreGhostShiftDriverId.map(id => parseInt(id));
                    filterAvailableDriverIds = filterAvailableDriverIds.filter(id => !integerIds.includes(id));
                }
                let messageString = "";
                let messageStringTemp = "";
                let isPublish = false;
                let isTemp = false;
                let dialogBoxStatus = false;
                let publishDrivers = [];

                if (availableDriverIds.length === 1) {
                    const driver = availableDriverIds[0];
                    if (driver.type === "ghostShift") {
                        dialogMsg = `Driver ${driver.name} already has a scheduled Ghost shift in ${driver.code}. \n You cannot create a new shift until you publish or discard the temporary changes to the current shift station shifts.`;
                        await alert({ text: dialogMsg, btnText: "Cancel" });
                        return false;
                    } else if (driver.type === "temp") {
                        dialogMsg = `Driver ${driver.name} already has a scheduled shift in ${driver.code}. You can't create the shift, please publish or discard temp changes to other station shifts before proceeding.`;
                        await alert({ text: dialogMsg, btnText: "Cancel" });

                        if (!filterAvailableDriverIds.length) return false;
                        dialogBoxStatus = true;
                        const foundDriver = options
                            .filter((driver) => filterAvailableDriverIds.includes(driver.id))
                            .map((driver) => driver.name)
                            .join(", ");
                        dialogMsg = `Are you sure you want to create a shift for the selected drivers: "${foundDriver}"?`;
                    } else {
                        publishDriverId.push(driver.driverId);
                        dialogMsg = `Driver ${driver.name} already has a scheduled shift in ${driver.code}. \n The original shift will be restored, and the current shift will be deleted.`;
                    }
                } else {
                    publishDrivers = availableDriverIds.filter((driver) => driver.type === "publish");
                    let tempDrivers = availableDriverIds.filter((driver) => driver.type === "temp");

                    if (publishDrivers.length) {
                        isPublish = true;
                        messageString = generateMessageTable(publishDrivers, "Publish Drivers");
                    }
                    if (tempDrivers.length) {
                        isTemp = true;
                        messageStringTemp = generateMessageTable(tempDrivers, "Temp Drivers");
                    }

                    const publishMsg = isPublish
                        ? `Some selected drivers already have scheduled shifts in other stations. Would you like to move these drivers anyway? ${messageString}`
                        : "";
                    const tempMsg = isTemp
                        ? `Some selected drivers already have scheduled shifts in other stations. You can't create the shift, please publish or discard temp changes to other station shifts before proceeding. ${messageStringTemp}`
                        : "";

                    if (isTemp && !isPublish) {
                        await alert({ text: tempMsg, btnText: "Cancel" });
                        if (!filterAvailableDriverIds.length) return false;

                        dialogBoxStatus = true;
                        const foundDriver = options
                            .filter((driver) => filterAvailableDriverIds.includes(driver.id))
                            .map((driver) => driver.name)
                            .join(", ");
                        dialogMsg = `Are you sure you want to create a shift for the selected drivers: "${foundDriver}"?`;
                    } else {
                        dialogMsg = tempMsg + publishMsg;
                    }
                }

                const dialogReturn = await dialog({
                    icon: <Icon.Warning color={Theme.colors.warnings.text} />,
                    text: dialogMsg,
                    modal: {
                        width: "390px",
                    },
                    buttons: [
                        { text: "Cancel", callback: async () => false },
                        { type: "primary", text: "Confirm" },
                    ],
                }).then(async (dialogResponse) => {
                    publishDriverId = publishDriverId.map(Number);
                    if (!dialogResponse) {
                        publishDriverId =
                            publishDriverId.length === 0
                                ? publishDrivers.map((driver) => Number(driver.driverId))
                                : publishDriverId;
                        let selectedDriverIds = filterAvailableDriverIds.filter(
                            (item) => !publishDriverId.includes(item)
                        );
                        if (selectedDriverIds.length && !dialogBoxStatus) {
                            const foundDriver = options
                                .filter((driver) => selectedDriverIds.includes(driver.id))
                                .map((driver) => driver.name)
                                .join(", ");
                            return await dialog({
                                text: `Are you sure you want to create a shift for the selected drivers: "${foundDriver}"?`,
                                buttons: [
                                    { text: "Cancel", callback: async () => false },
                                    { type: "primary", text: "Yes" },
                                ],
                            }).then(async (response) => {
                                if (!response) return false;
                                return selectedDriverIds;
                            });
                        }
                        return false;
                    }

                    return dialogBoxStatus
                        ? filterAvailableDriverIds
                        : [...new Set([...publishDriverId, ...filterAvailableDriverIds])];
                });
                return dialogReturn;
            }
            // remove ghost shift drivers 
            const filteredIds = driverIds.filter(id => !ignoreGhostShiftDriverId.includes(id));
            if (filteredIds.length === 0) return false;
            return filteredIds;
        } else {
            if (ghostShiftDrivers.length > 0) {
                let driverInfo = ghostShiftDrivers[0];
                dialogMsg = `${driverInfo.driverName} already has a scheduled Ghost shift at the ${driverInfo.stationCode} station. You cannot assign an additional shift.`;
                await alert({ text: dialogMsg, btnText: "Cancel" });
                return false;
            }
            // Check if driver is already scheduled
            if (availableDriverIds.length > 0) {
                const driverInfo = availableDriverIds[0];

                // If the shift has already happened
                if (isTodayPast) {
                    await showAlert(
                        `Driver ${driverInfo.name} already has a scheduled shift in ${driverInfo.code}. \n You can't move a shift that already happened.`
                    );
                    return false;
                }

                // If the shift creation is successful
                if (success) {
                    let dialogMsg;

                    // Temp driver scenario
                    if (driverInfo.type === "temp") {
                        dialogMsg = `Driver ${driverInfo.name} already has a scheduled shift in ${driverInfo.code}. \n You can't create the shift. Please publish or discard temp changes to other station shifts before proceeding.`;

                        await showAlert(dialogMsg);
                        return false;
                    } else if (driverInfo.type === "ghostShift") {
                        dialogMsg = `${driverInfo.name} already has a scheduled Ghost shift at the ${driverInfo.code} station. You cannot assign an additional shift.`;
                        await showAlert(dialogMsg);
                        return false;
                    } else if (driverInfo.type === "publish" && driverInfo.countShift > 1) {
                        dialogMsg = `${driverInfo.name} already has multiple shifts scheduled at the ${driverInfo.code} station. No additional shifts can be assigned.`;
                        await showAlert(dialogMsg);
                        return false;
                    }

                    // Regular shift scenario
                    const destinationStationCode = stationObject?.code ?? "the selected station";
                    dialogMsg = `Are you sure you want to recall ${driverInfo.name} from their ${driverInfo.code} shift on ${moment(date).format("dddd, MMMM Do")}? They will be moved back to ${destinationStationCode} and their original shift will be deleted.`;

                    const dialogResponse = await showConfirmationDialog(dialogMsg);
                    if (!dialogResponse) return false;

                    return true;
                }
            }
        }

        return true;
    };
    return {
        canceler,
        handleGhostShift,
    };
};

export default useGhostShiftApi;
